import { arrayMove } from '@dnd-kit/sortable';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DndArticleListSection from './location-info-order/DndArticleListSection';
import CloseIcon from '@mui/icons-material/Close';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import { useParams } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
	useGetGalleryTreasureLocationPageQuery,
	useSortGalleryTreasureLocationMutation,
} from '@/api/treasury/galleryApiSlice';
import ViewLocationDialog from './ViewLocationDialog';
import {
	useGetPuzzleTreasureLocationPageQuery,
	useSortPuzzleTreasureLocationMutation,
} from '@/api/treasury/puzzleApiSlice';

interface Props {
	bgImage: string;
}

export default function SetLocationOrderDialog(props: Props) {
	const { bgImage } = props;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [sortPuzzleTreasureLocation] = useSortPuzzleTreasureLocationMutation();
	const { puzzleId } = useParams();

	const [meditationItems, setMeditationItems] = useState<any>([]);

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetPuzzleTreasureLocationPageQuery(puzzleId || '', {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	useEffect(() => {
		const list = Array.isArray(propertySummary?.data)
			? propertySummary?.data
			: [];
		setMeditationItems(list);
	}, [isGetPropertySummarySuccess, propertySummary]);

	const handleRepositionItem = (activeIndex: number, overIndex: number) => {
		setMeditationItems((prev: unknown[]) =>
			arrayMove(prev, activeIndex, overIndex)
		);
	};

	const onSubmit = async () => {
		const response = await sortPuzzleTreasureLocation(
			meditationItems.map((item: { id: string }) => item.id)
		);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.sortSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<FormatLineSpacingIcon />}
				onClick={() => setOpen(true)}
			>
				{t('galleryList.layerLocation')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('galleryList.layerLocation')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Box
						display='flex'
						justifyContent='space-between'
						alignItems='center'
					>
						<Typography
							variant='body2'
							sx={{
								display: 'flex',
							}}
						>
							<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
							&nbsp;
							{t('galleryList.sortlayerLocation')}
						</Typography>
					</Box>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={1}>
						<Grid item xs display='flex'>
							<Stack>
								{meditationItems && (
									<DndArticleListSection
										meditationItems={meditationItems}
										handleRepositionItem={handleRepositionItem}
									/>
								)}
							</Stack>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions sx={{ position: 'relative' }}>
					<ViewLocationDialog list={meditationItems} bgImage={bgImage} />
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
